import type { Rules } from "odyssey-shared";
import { say } from "../../words.ts";
import { slotLabel } from "../bag/describe.ts";

/**
 * The loot window's wording that names an item or a stack (P2.8-T16, ADR-091 decisions 19 and 22). The words are the
 * language files' (`chest.*` in `odyssey-lang`). Every item name is carried as text and drawn as text, never as
 * markup.
 */

/**
 * An item's name as a row prints it; for an id the page's rules lack, which a server of the same rules never sends,
 * "Unknown item".
 *
 * @param rules The page's rules.
 * @param item The item's rules id.
 * @returns The name.
 * @example
 * itemName(rules, "minor_healing_potion");
 * // "Minor Healing Potion"
 */
export function itemName(rules: Rules, item: string): string {
  return rules.items.get(item)?.name ?? say("bag.unknownItem");
}

/**
 * A row's Take button's accessible name, which says what it takes and, for a line refused for want of room, why it
 * cannot be pressed.
 *
 * @param rules The page's rules.
 * @param item The line's item.
 * @param quantity The line's quantity.
 * @param full Whether the line was refused for want of room.
 * @returns The name.
 * @example
 * takeLabel(rules, "minor_healing_potion", 3, true);
 * // "Take Minor Healing Potion, 3: your bags are full"
 */
export function takeLabel(rules: Rules, item: string, quantity: number, full: boolean): string {
  const what = say("chest.takeItem", { take: { k: "chest.take" }, item: slotLabel(rules, item, quantity) });
  return full ? say("chest.takeFull", { what }) : what;
}

/**
 * The loot toast's title (the design's "Iron filings x4"): the item's name, with its count above one.
 *
 * @param rules The page's rules.
 * @param item The item gained.
 * @param quantity How many.
 * @returns The title.
 * @example
 * toastTitle(rules, "minor_healing_potion", 3);
 * // "Minor Healing Potion x3"
 */
export function toastTitle(rules: Rules, item: string, quantity: number): string {
  const name = itemName(rules, item);
  return quantity > 1 ? say("chest.stack", { name, quantity: String(quantity) }) : name;
}

/**
 * The second line of the notice of lines a full bag could not take (P2.8-T62, the design's `loot-notices` card, its
 * `full` state): each line as a toast's title names it, joined by commas.
 *
 * @param rules The page's rules.
 * @param lost The lines, each an item and a quantity.
 * @returns The line.
 * @example
 * lostLine(rules, [["minor_healing_potion", 2]]);
 * // "Minor Healing Potion x2"
 */
export function lostLine(rules: Rules, lost: readonly (readonly [string, number])[]): string {
  return lost.map(([item, quantity]) => toastTitle(rules, item, quantity)).join(", ");
}
